/**
 * GO Commerce GraphQL Cache Utility
 * 
 * Provides caching for GraphQL queries to reduce redundant API calls.
 * Integrates with the existing apiCache system to provide consistent caching behavior.
 */

import { apiCache } from './apiCache';
import { useRuntimeConfig } from '#app';
import { $fetch } from 'ofetch';

// Cache time configurations for common GraphQL queries
const CACHE_TIMES = {
  GET_GLOBAL_SETTINGS: 15 * 60 * 1000, // 15 minutes for global settings
  GET_PRODUCTS: 30 * 60 * 1000,        // 30 minutes for products
  GET_USER_DATA: 5 * 60 * 1000,         // 5 minutes for user data
};

// Define operation types for type safety
type GraphQLOperation = {
  query: string;
  variables?: Record<string, any>;
  operationName?: string;
};

// Utility function to identify query type by content
function identifyQueryType(query: string): string {
  if (query.includes('GetGlobalSettings')) return 'GET_GLOBAL_SETTINGS';
  if (query.includes('GetProducts')) return 'GET_PRODUCTS';
  if (query.includes('GetUserData')) return 'GET_USER_DATA';
  return 'UNKNOWN';
}

// Generate a cache key based on the operation
function generateCacheKey(operation: GraphQLOperation): string {
  const { query, variables, operationName } = operation;
  
  // Identify query type for appropriate caching
  const queryType = identifyQueryType(query);
  
  // For known query types, we can be more aggressive with caching
  // and ignore certain variables that don't affect the output
  if (queryType === 'GET_GLOBAL_SETTINGS') {
    // Global settings don't depend on variables, so we can use a simpler key
    return `graphql_global_settings`;
  }
  
  // For other queries, include variables in the cache key to ensure uniqueness
  const variablesStr = variables ? JSON.stringify(variables) : '';
  const opName = operationName || 'query';
  
  return `graphql_${queryType.toLowerCase()}_${opName}_${variablesStr}`;
}

// Get the appropriate TTL for a query
function getQueryTTL(operation: GraphQLOperation): number {
  const { query } = operation;
  const queryType = identifyQueryType(query);
  
  // Return appropriate TTL based on query type
  return CACHE_TIMES[queryType as keyof typeof CACHE_TIMES] || 5 * 60 * 1000; // Default 5 minutes
}

/**
 * Execute a GraphQL query with caching
 * 
 * @param operation The GraphQL operation to execute
 * @param ttl Time-to-live for the cache in ms (defaults to 5 minutes)
 * @param forceFresh Whether to force a fresh request
 * @returns The result of the GraphQL query
 */
export const executeGraphQLQuery = async <T>(
  operation: GraphQLOperation,
  ttl: number = 5 * 60 * 1000, // 5 minutes default cache TTL
  forceFresh = false
): Promise<T> => {
  // Create cache key
  const operationName = operation.operationName || 'anonymous';
  const variables = operation.variables || {};
  const cacheKey = `graphql:${operationName}:${JSON.stringify(variables)}`;
  
  // Determine if this query should use shared caching
  // Shared caching prevents duplicate in-flight requests for the same data
  const sharedCacheOperations = [
    'GetGlobalSettings',
    'GetCurrentServices',
    'GetMenuItems',
    'GetFAQs',
    'GetCustomerData'
  ];
  
  const useSharedCache = sharedCacheOperations.includes(operationName);
  
  // In server-side rendering, we want a shorter TTL
  // This ensures data isn't stale but still allows for some caching benefit
  let effectiveTTL = ttl;
  if (process.server) {
    const ssrTTL = 30 * 1000; // 30 second TTL for SSR
    effectiveTTL = Math.min(ttl, ssrTTL); // Use the shorter of the two
    console.log(`⏳ SSR mode detected, using shorter TTL of ${effectiveTTL}ms for ${operationName}`);
  }
  
  // Log GraphQL operation for debugging
  console.log(`📊 GraphQL [${operationName}]${useSharedCache ? ' 🔄 SHARED' : ''} with variables: ${JSON.stringify(variables)}`);

  // Use operation-specific TTLs for frequently accessed data
  if (operationName === 'GetGlobalSettings') {
    effectiveTTL = 30 * 60 * 1000; // 30 minutes
    console.log('🌐 Using extended TTL for GlobalSettings query');
  } else if (operationName === 'GetMenuItems') {
    effectiveTTL = 15 * 60 * 1000; // 15 minutes
  } else if (operationName === 'GetFAQs') {
    effectiveTTL = 60 * 60 * 1000; // 1 hour
  }
  
  // Execute with caching
  const result = await apiCache.fetch<T>(cacheKey, async () => {
    return executeGraphQLQueryRaw<T>(operation);
  }, { 
    ttl: effectiveTTL, 
    forceFresh,
    useSharedCache // Use shared caching for frequent operations
  });
  
  return result;
};

/**
 * Execute a GraphQL query without caching (internal function)
 */
const executeGraphQLQueryRaw = async <T>(operation: GraphQLOperation): Promise<T> => {
  // Generate a unique request ID for correlation
  const requestId = Math.random().toString(36).substring(2, 10);
  const startTime = Date.now();
  
  try {
    console.log(`[GraphQLCache:${requestId}] 🚀 Executing GraphQL query: ${operation.operationName || 'anonymous'}`);
    
    let result: any;
    
    if (process.server) {
      // SSR MODE: Call server handler directly (no HTTP calls)
      console.log(`[GraphQLCache:${requestId}] SSR: Using internal server handler`);
      
      // Import the server handler logic directly in SSR
      const config = useRuntimeConfig();
      const STRAPI_URL = config.STRAPI_URL;
      const STRAPI_API_TOKEN = config.STRAPI_API_TOKEN;
      
      if (!STRAPI_URL || !STRAPI_API_TOKEN) {
        throw new Error('Server configuration error: Missing STRAPI_URL or STRAPI_API_TOKEN');
      }
      
      // Make direct backend call in SSR (server-to-server)
      const strapiResponse = await fetch(`${STRAPI_URL}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${STRAPI_API_TOKEN}`,
        },
        body: JSON.stringify(operation),
      });
      
      if (!strapiResponse.ok) {
        const errorText = await strapiResponse.text();
        console.error(`[GraphQLCache:${requestId}] ❌ Strapi request failed:`, errorText);
        throw new Error(`Strapi GraphQL query failed with status: ${strapiResponse.status}`);
      }
      
      result = await strapiResponse.json();
      
    } else {
      // CLIENT MODE: Use HTTP proxy as before
      console.log(`[GraphQLCache:${requestId}] Client: Using proxy endpoint`);
      
      const response = await fetch('/api/strapi/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(operation),
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[GraphQLCache:${requestId}] ❌ Proxy request failed:`, errorText);
        throw new Error(`GraphQL query failed with status: ${response.status}`);
      }
      
      result = await response.json();
    }
    
    // Enhanced debugging for development environments
    if (process.env.NODE_ENV === 'development') {
      console.log(`[GraphQLCache:${requestId}] Environment: ${process.server ? 'Server (SSR)' : 'Client'}`);
    }
    
    // Check for GraphQL errors
    if (result.errors && result.errors.length > 0) {
      console.error(`[GraphQLCache:${requestId}] ❌ GraphQL errors:`, result.errors);
      throw new Error(`GraphQL errors: ${result.errors[0].message}`);
    }
    
    const duration = Date.now() - startTime;
    console.log(`[GraphQLCache:${requestId}] ✅ Query completed in ${duration}ms`);
    
    // Return the data
    return result.data as T;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[GraphQLCache:${requestId}] ❌ Query failed after ${duration}ms:`, error);
    throw error;
  }
}

/**
 * Invalidate a specific GraphQL query cache
 * 
 * @param queryType The type of query to invalidate (e.g., 'GET_GLOBAL_SETTINGS')
 */
export function invalidateGraphQLCache(queryType: string): void {
  // Skip in SSR context
  if (typeof window === 'undefined') return;
  
  switch (queryType) {
    case 'GET_GLOBAL_SETTINGS':
      apiCache.invalidate('graphql_global_settings');
      break;
    default:
      // For other types, we can't easily invalidate without knowing variables
      // So we invalidate all graphql caches
      invalidateAllGraphQLCache();
  }
}

/**
 * Invalidate all GraphQL query caches
 */
export function invalidateAllGraphQLCache(): void {
  // Skip in SSR context
  if (typeof window === 'undefined') return;
  
  // Get all cache keys
  const cacheStorage = localStorage.getItem('go_api_cache');
  if (!cacheStorage) return;
  
  try {
    const cacheData = JSON.parse(cacheStorage);
    
    // Find all GraphQL cache keys
    Object.keys(cacheData).forEach(key => {
      if (key.startsWith('api_cache_graphql_')) {
        apiCache.invalidate(key.replace('api_cache_', ''));
      }
    });
    
    console.log('Invalidated all GraphQL cache entries');
  } catch (e) {
    console.error('Error invalidating GraphQL cache:', e);
  }
}
